/**
 * Отказы бэка «действие недоступно для этого трека» → текст для человека.
 *
 * Кнопки недоступных действий фронт прячет заранее (см.
 * trackActionAvailability), но запрос всё равно может уйти: гонка, старый кэш
 * списка, прямая ссылка из бота. Тогда бэк отвечает 409 со строкой-кодом в
 * `detail` (тем же кодом воркер закрывает заказ — он приезжает в `reason`
 * уведомления). Сырой код или общее «не удалось» человеку ничего не объясняют.
 *
 * Единственное место маппинга кодов в тексты. Модуль чистый — см.
 * trackActionErrors.test.ts.
 */
export const TRACK_ACTION_ERROR_CODES = [
    "make_instrumental_not_supported_for_track",
    "download_wav_not_supported_for_track",
    "extend_not_supported_for_track",
    "replace_section_not_supported_for_track",
    "karaoke_not_supported_for_track",
    "voice_persona_not_supported_for_track",
    "video_cover_no_track_cover",
    "video_cover_text_not_supported_for_track",
    "release_not_supported_for_track",
    "release_cover_required",
];
const TEXTS = {
    make_instrumental_not_supported_for_track: {
        ru: "Для этого трека минус недоступен.",
        en: "An instrumental isn't available for this track.",
    },
    download_wav_not_supported_for_track: {
        ru: "Для этого трека WAV недоступен — скачайте MP3.",
        en: "WAV isn't available for this track — download the MP3 instead.",
    },
    extend_not_supported_for_track: {
        ru: "Для этого трека продление недоступно.",
        en: "Extending isn't available for this track.",
    },
    replace_section_not_supported_for_track: {
        ru: "Для этого трека замена части недоступна.",
        en: "Section replacement isn't available for this track.",
    },
    karaoke_not_supported_for_track: {
        ru: "Для этого трека караоке недоступно.",
        en: "Karaoke isn't available for this track.",
    },
    voice_persona_not_supported_for_track: {
        ru: "Из этого трека нельзя создать ИИ-голос.",
        en: "An AI voice can't be created from this track.",
    },
    video_cover_no_track_cover: {
        ru: "У трека нет обложки — выберите своё фото или слайдшоу.",
        en: "The track has no cover — choose your own photo or a slideshow.",
    },
    video_cover_text_not_supported_for_track: {
        ru: "Для этого трека текст на видео недоступен — выберите «Без текста».",
        en: "Lyrics on video aren't available for this track — choose “No lyrics”.",
    },
    release_not_supported_for_track: {
        ru: "Для этого трека релиз на площадках недоступен.",
        en: "Releasing on streaming platforms isn't available for this track.",
    },
    release_cover_required: {
        ru: "У трека нет обложки — загрузите свою.",
        en: "The track has no cover — upload your own.",
    },
};
function detailText(detail) {
    if (typeof detail === "string")
        return detail;
    if (detail != null && typeof detail === "object") {
        try {
            return JSON.stringify(detail);
        }
        catch {
            return "";
        }
    }
    return "";
}
/** Код целиком, а не как часть другого идентификатора. */
function containsCode(text, code) {
    let from = 0;
    for (;;) {
        const at = text.indexOf(code, from);
        if (at < 0)
            return false;
        const before = at > 0 ? text[at - 1] : "";
        const after = text[at + code.length] ?? "";
        if (!/[a-z0-9_]/i.test(before) && !/[a-z0-9_]/i.test(after))
            return true;
        from = at + 1;
    }
}
/**
 * Код отказа из `detail` ответа либо `reason` уведомления/WS. Бэк отдаёт
 * строку; объект (на случай смены формата) разбираем по его JSON. Не наш код →
 * null.
 */
export function trackActionErrorCode(detail) {
    const text = detailText(detail);
    if (!text)
        return null;
    return TRACK_ACTION_ERROR_CODES.find((code) => containsCode(text, code)) ?? null;
}
/** То же по ошибке запроса (axios): код лежит в `response.data.detail`. */
export function trackActionErrorCodeOf(error) {
    const detail = error
        ?.response?.data?.detail;
    return trackActionErrorCode(detail);
}
export function trackActionErrorText(code, ru) {
    return ru ? TEXTS[code].ru : TEXTS[code].en;
}
/**
 * Текст отказа по ошибке запроса; null — ошибка не из этого списка (вызывающий
 * покажет свой общий текст).
 */
export function trackActionErrorMessage(error, ru) {
    const code = trackActionErrorCodeOf(error);
    return code ? trackActionErrorText(code, ru) : null;
}
/** То же по `reason` уведомления/WS-события. */
export function trackActionReasonMessage(reason, ru) {
    const code = trackActionErrorCode(reason);
    return code ? trackActionErrorText(code, ru) : null;
}
